import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getContractForMission, sendContractForMission } from '../../services/missionService';
import { getMission, updateMission } from '../../services/missionService';
import html2pdf from 'html2pdf.js';
import { useI18n } from '../../i18n/I18nContext';

const ContractView = () => {
  const { t } = useI18n();
  const { missionId } = useParams();
  const [searchParams] = useSearchParams();
  const transporteurId = searchParams.get('transporteurId');

  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [mission, setMission] = useState(null);

  const contractRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [contractRes, missionRes] = await Promise.all([
          getContractForMission(missionId, transporteurId),
          getMission(missionId),
        ]);
        setHtml(contractRes?.html || '');
        setMission(missionRes);
      } catch (e) {
        setError(t('contracts.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [missionId, transporteurId, t]);

  const handleExportPDF = () => {
    const element = contractRef.current;

    html2pdf()
      .from(element)
      .set({
        filename: `contrat_mission_${missionId}.pdf`,
        margin: 0,
        pagebreak: { mode: ['css', 'legacy'] },
        html2canvas: {
          scale: 3,
          scrollX: 0,
          scrollY: 0,
          windowWidth: element.scrollWidth
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait'
        }
      })
      .save();
  };

  const handleSaveChanges = async () => {
    if (!missionId || !mission) return;
    if (!contractRef.current) return;

    const container = contractRef.current;
    const priceEl = container.querySelector('[data-field="price"]');
    const currencyEl = container.querySelector('[data-field="currency"]');
    const paymentEl = container.querySelector('[data-field="paymentTerms"]');

    const priceText = priceEl ? priceEl.textContent || '' : '';
    const currencyText = currencyEl ? currencyEl.textContent || '' : '';
    const paymentText = paymentEl ? paymentEl.textContent || '' : '';

    const amountMatch = priceText.match(/([0-9]+(?:[.,][0-9]+)?)/);
    const amount = amountMatch ? parseFloat(amountMatch[1].replace(',', '.')) : null;
    const currencyMatch = currencyText.match(/\b(EUR|MAD|USD)\b/i);
    const currency = (currencyMatch ? currencyMatch[1] : 'EUR').toUpperCase();

    setSaving(true);
    try {
      const updatedMission = {
        ...mission,
        price: amount,
      };
      const res = await updateMission(missionId, updatedMission);
      setMission(res);
      return { amount, currency, paymentTerms: paymentText };
    } catch (e) {
      setError(e.message || t('contracts.errors.saveFailed'));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!missionId || !transporteurId) return;
    setSending(true);
    try {
      let currency = 'EUR';
      if (mission) {
        const result = await handleSaveChanges();
        if (result && result.currency) {
          currency = result.currency;
        }
      }
      await sendContractForMission(missionId, transporteurId, currency);
      alert(t('contracts.success.sent'));
    } catch (e) {
      setError(e.message || t('contracts.errors.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="container py-3">
        {loading && <p>{t('common.loading')}</p>}
        {error && <div className="alert alert-danger">{error}</div>}

        {!loading && !error && (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <button className="btn btn-secondary" onClick={handleExportPDF}>
                {t('contracts.view.exportPdf')}
              </button>
              <button className="btn btn-outline-primary" onClick={handleSaveChanges} disabled={saving}>
                {saving ? t('common.saving') : t('contracts.view.saveChanges')}
              </button>
              <button className="btn btn-primary" onClick={handleSend} disabled={sending}>
                {sending ? t('common.sending') : t('contracts.view.send')}
              </button>
            </div>

            <div
              ref={contractRef}
              style={{
                width: '210mm',
                margin: '0 auto',
                background: '#fff'
              }}
            >
              <div
                suppressContentEditableWarning
                dangerouslySetInnerHTML={{ __html: html }}
              />
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ContractView;
